import { DocumentIcon } from '@heroicons/react/24/outline';

import { useGetFirmsQuery } from '../../services/api/firmsService';
import { MatterType } from '../../services/types/client-intake-types';
import { DemandExample } from '../../services/types/demand-example-types';
import { Button } from '../ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '../ui/catalyst/dialog';
import { Field, Fieldset, Label } from '../ui/catalyst/fieldset';
import { Input } from '../ui/catalyst/input';
import { Select } from '../ui/catalyst/select';
import { Text } from '../ui/catalyst/text';
import { Textarea } from '../ui/catalyst/textarea';
import { TagInput } from './TagInput';

interface FormData {
  title: string;
  description: string;
  matter_type: MatterType | '';
  jurisdiction: string[];
  practice_areas: string[];
  tags: string[];
  firm_id: string | null;
  transform_id: string;
}

interface EditDemandExampleModalProps {
  isOpen: boolean;
  selectedExample: DemandExample | null;
  formData: FormData;
  isLoading: boolean;
  onClose: () => void;
  onSubmit: () => void;
  onFormChange: (field: keyof FormData, value: any) => void;
}

export const EditDemandExampleModal = ({
  isOpen,
  selectedExample,
  formData,
  isLoading,
  onClose,
  onSubmit,
  onFormChange,
}: EditDemandExampleModalProps) => {
  // Fetch firms for the dropdown
  const {
    data: firms,
    isLoading: firmsLoading,
    error: firmsError,
  } = useGetFirmsQuery({});

  // Don't render if no example is selected
  if (!selectedExample) {
    return null;
  }

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle>Edit Demand Example</DialogTitle>
      <DialogDescription>
        Update the demand example details below. To change the file, create a
        new example.
      </DialogDescription>

      <DialogBody>
        <Fieldset className="space-y-6">
          <Field>
            <Label>Title</Label>
            <Input
              value={formData.title}
              onChange={(e) => onFormChange('title', e.target.value)}
              placeholder="Enter example title"
            />
          </Field>

          <Field>
            <Label>Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => onFormChange('description', e.target.value)}
              placeholder="Enter description"
              rows={3}
            />
          </Field>

          <Field>
            <Label>Firm</Label>
            <Select
              value={formData.firm_id ?? undefined}
              onChange={(e) => onFormChange('firm_id', e.target.value)}
              disabled={firmsLoading}
            >
              <option value="">
                {firmsLoading ? 'Loading firms...' : 'Select firm'}
              </option>
              {firmsError && (
                <option value="" disabled>
                  Error loading firms
                </option>
              )}
              {firms?.map((firm) => (
                <option key={firm.id} value={firm.id}>
                  {firm.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field>
            <Label>Matter Type</Label>
            <Select
              value={formData.matter_type}
              onChange={(e) =>
                onFormChange('matter_type', e.target.value as MatterType)
              }
            >
              <option value="">Select matter type</option>
              {Object.values(MatterType).map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </Select>
          </Field>

          <Field>
            <Label>Jurisdiction</Label>
            <div className="pt-2">
              <TagInput
                tags={formData.jurisdiction}
                onChange={(tags) => onFormChange('jurisdiction', tags)}
                placeholder="e.g., California, Los Angeles County"
              />
            </div>
          </Field>

          <Field>
            <Label>Tags</Label>
            <div className="pt-2">
              <TagInput
                tags={formData.tags}
                onChange={(tags) => onFormChange('tags', tags)}
                placeholder="e.g., settlement, medical expenses"
              />
            </div>
          </Field>

          {selectedExample.demand && (
            <Field>
              <Label>Current File</Label>
              <div className="flex items-center gap-2 p-3 bg-zinc-50 rounded-lg">
                <DocumentIcon className="h-5 w-5 text-zinc-500" />
                <Text className="text-sm text-zinc-700">
                  {selectedExample.title || 'Untitled'}.pdf
                </Text>
                <Text className="text-xs text-zinc-500">
                  (To change the file, create a new example)
                </Text>
              </div>
            </Field>
          )}
        </Fieldset>
      </DialogBody>

      <DialogActions>
        <Button outline onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={onSubmit} disabled={isLoading}>
          {isLoading ? 'Updating...' : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
